import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation, Link } from "react-router-dom"
import moment from "moment"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Grid, Box, Typography, Tooltip, CircularProgress, IconButton } from "@material-ui/core"
import AttachmentIcon from "../../../icons/AttachamentIcon"
import { operationsHelper } from "../../../../helpers/operations"
import { getFileRowClickRequest, getFileRequestByPage } from "../../../../actions/documentModalActions"
import { RotateLeftImage, getAnnotation, getAuditHistory, updateViewerPageNumber, zoomInImage } from "../../../../actions/viewerActions"
import Annotations from "../../additionalDetails/Annotations"
import { ANNO_BG_COLOR } from "../../../../utils"
import { getReviewBasedOnFileIdRequest } from "../../../../actions/reviewActions"
import { searchHighlightFilter } from "../../../../actions/highlightActions"
import Modal from "@material-ui/core/Modal"
import Backdrop from "@material-ui/core/Backdrop"
import Fade from "@material-ui/core/Fade"
import Zoom from "@material-ui/core/Zoom"
import axios from "../../../../services/axios"
import CloseIcon from "@material-ui/icons/Close"

const useStyles = makeStyles((theme) => ({
	root: {
		...theme.typography.body2,
		backgroundColor: theme.palette.common.white,
		borderRadius: "5px",
		padding: "16px 23px",
		whiteSpace: "pre-line",
		maxHeight: "80vh",
		height: "65vh",
		overflow: "auto",
		display: "flex",
		textAlign: "center",
		justifyContent: "center",
		alignItems: "center",
	},
	modal: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		margin: "auto",
		width: "100%",
		height: "100%",
	},
	closeButton: {
		position: "absolute",
		right: "5px",
		top: "5px",
		color: theme.palette.grey[500],
		width: "10px",
		height: "10px",
		zIndex: "1",
	},
	paper: {
		backgroundColor: theme.palette.background.paper,
		margin: "20px",
		overflow: "hidden",
		position: "relative",
	},

	zoom: {
		transformOrigin: "top left",
	},

	sam: {
		transformOrigin: "center center",
	},
}))

const PictureViewer = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const history = useHistory()
	const theme = useTheme()
	const imgRef = React.createRef()
	const modalRef = React.createRef()
	const [t] = useTranslation("global")
	const location = useLocation()
	const [open, setOpen] = useState(false)
	const [imageDimension, setImageDimension] = useState({})
	const [base64, setBase64] = useState(false)
	const [rotation, setRotation] = useState(0)
	const [scale, setScale] = useState(1)
	function isEven(n) {
		return Math.abs(n) % 2 == 0
	}
	const handleOpen = () => {
		setImageDimension({
			width: isEven(rotation / 90)
				? Math.min(window.innerWidth, imageDimension?.width)
				: Math.min(window.innerHeight, imageDimension?.height),
			height: isEven(rotation / 90)
				? Math.min(window.innerHeight, imageDimension?.height)
				: Math.min(window.innerWidth, imageDimension?.width),
		})
		setOpen(true)
	}
	const handleClose = () => setOpen(false)
	const { documentDetails, currentDocument } = useSelector((state) => state.documentModal)
	const { imageZoomScale, imageRotatePosition } = useSelector((state) => state.viewerData)
	useEffect(() => {
		const params = location?.pathname?.split("/")
		const fileId = params[params.length - 1]
		if (documentDetails?.size <= 70000000) {
			axios
				.get(`/file/download/${fileId}`, { responseType: "blob" })
				.then((response) => {
					if (response?.data) {
						setBase64(response?.data)
						const data = response.data
						const fileType = { type: "image/png" }
						const blob = new Blob([data], fileType)
						const url = URL.createObjectURL(blob)
						const img = new Image()
						img.src = url
						img.onload = () => {
							console.log(img.width, img.height)
							setImageDimension({ width: img.width, height: img.height })
						}
						setBase64(url)
					} else {
						setBase64("...")
					}
				})
				.catch(() => {
					setBase64("...")
				})
		}
	}, [documentDetails])

	useEffect(() => {
		dispatch(zoomInImage(1.0))
		dispatch(RotateLeftImage(0))
	}, [])

	useEffect(() => {
		setScale(imageZoomScale)
	}, [imageZoomScale])

	useEffect(() => {
		setRotation(imageRotatePosition)
	}, [imageRotatePosition])

	return (
		<Box className={classes.root} id="viewer-container">
			<Box onClick={handleOpen} style={{ cursor: "pointer" }}>
				{documentDetails?.size >= 70000000 ? (
					<div
						style={{
							alignItems: "center",
							display: "flex",
							justifyContent: "center",
						}}
					>
						<div
							style={{
								borderRadius: "0.25rem",
								padding: "0.5rem",
							}}
						>
							<div>{t("largeImageFile")}</div>
						</div>
					</div>
				) : (
					<Tooltip enterDelay={1000} leaveDelay={200} title={"Click to view in fullscreen"} placement="top">
						{base64 ? (
							<div
								className={classes.zoom}
								style={{
									maxWidth: "100%",
									maxHeight: "60vh",
									height: "auto",
									width: "auto",
									padding: 0,
									margin: 0,
									display: "inlineBlock",
									transition: `transform 0.5s ease-in-out`,
									transform: `scale(${scale})`,
									transformOrigin: "top left",
								}}
							>
								<img
									className={classes.sam}
									style={{
										maxWidth: "100%",
										maxHeight: "60vh",
										height: "auto",
										width: "auto",
										padding: 0,
										margin: 0,
										display: "inlineBlock",
										transition: `transform 0.5s ease-in-out`,
										transform: `rotate(${rotation}deg)`,
									}}
									ref={imgRef}
									src={base64}
								/>
							</div>
						) : (
							<div
								style={{
									display: "flex",
									textAlign: "center",
									verticalAlign: "middle",
								}}
							>
								<div
									style={{
										alignItems: "center",
										display: "flex",
										justifyContent: "center",
									}}
								>
									<div
										style={{
											borderRadius: "0.25rem",
											padding: "0.5rem",
										}}
									>
										<CircularProgress />
									</div>
								</div>
							</div>
						)}
					</Tooltip>
				)}
			</Box>
			<Modal
				aria-labelledby="transition-modal-title"
				aria-describedby="transition-modal-description"
				className={classes.modal}
				open={open}
				onClose={handleClose}
				closeAfterTransition
				BackdropComponent={Backdrop}
				BackdropProps={{
					timeout: 500,
				}}
			>
				<Zoom in={open}>
					<div className={classes.paper}>
						<IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
							<CloseIcon />
						</IconButton>
						<img
							style={{
								width: imageDimension?.width,
								height: imageDimension?.height,
								maxWidth: "100%",
								maxHeight: "100%",
								transition: `transform 0.5s ease-in-out`,
								transform: `rotate(${rotation}deg)`,
								objectFit: "contain",
							}}
							ref={modalRef}
							src={base64}
						/>
					</div>
				</Zoom>
			</Modal>
		</Box>
	)
}

export default PictureViewer
